import React, { useState, useEffect } from "react";
import "../styles/VerificationModal.css";
import { useAuth } from "../context/AuthContext";
import { usePayment } from "../context/PaymentContext";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaClock, FaTimes, FaUpload, FaEye } from "react-icons/fa";

import { getApiUrl } from "../utils/config";
const API_URL = getApiUrl();

const VerificationModal = ({ onClose }) => {
    const { showNotification } = useAuth();
    const { fetchVerificationStatus: fetchVerificationStatusContext } = usePayment();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [verificationData, setVerificationData] = useState(null);

    // Форма для подачи заявки
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        middleName: "",
        birthDate: ""
    });

    // Файлы
    const [documentPhoto, setDocumentPhoto] = useState(null);
    const [selfiePhoto, setSelfiePhoto] = useState(null);
    const [documentPreview, setDocumentPreview] = useState(null);
    const [selfiePreview, setSelfiePreview] = useState(null);

    // Состояние для просмотра изображений
    const [viewingImage, setViewingImage] = useState(null);

    // Загрузка статуса верификации при открытии модального окна
    useEffect(() => {
        fetchVerificationStatus();
    }, []);

    const fetchVerificationStatus = async () => {
        try {
            const response = await fetch(`${API_URL}/api/verification/status`, {
                method: "GET",
                credentials: "include",
            });
            const data = await response.json();

            if (data.success) {
                setVerificationData(data);
            } else {
                showNotification("Ошибка при загрузке данных верификации", "error");
            }
        } catch (error) {
            console.error('Ошибка при загрузке статуса верификации:', error);
            showNotification("Ошибка при загрузке данных верификации", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверка типа файла
        if (!file.type.startsWith('image/')) {
            showNotification("Можно загружать только изображения", "error");
            return;
        }

        // Проверка размера файла (10MB)
        if (file.size > 10 * 1024 * 1024) {
            showNotification("Размер файла не должен превышать 10MB", "error");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            if (type === 'document') {
                setDocumentPhoto(file);
                setDocumentPreview(e.target.result);
            } else {
                setSelfiePhoto(file);
                setSelfiePreview(e.target.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        // Валидация
        if (!formData.firstName || !formData.lastName || !formData.birthDate) {
            showNotification("Заполните все обязательные поля", "error");
            setSubmitting(false);
            return;
        }

        if (!documentPhoto || !selfiePhoto) {
            showNotification("Загрузите фото документа и селфи", "error");
            setSubmitting(false);
            return;
        }

        try {
            const submitData = new FormData();
            submitData.append('firstName', formData.firstName);
            submitData.append('lastName', formData.lastName);
            submitData.append('middleName', formData.middleName);
            submitData.append('birthDate', formData.birthDate);
            submitData.append('documentPhoto', documentPhoto);
            submitData.append('selfiePhoto', selfiePhoto);

            const response = await fetch(`${API_URL}/api/verification/submit`, {
                method: "POST",
                credentials: "include",
                body: submitData
            });

            const data = await response.json();

            if (data.success) {
                showNotification("Заявка на верификацию успешно подана", "success");
                // Перезагружаем данные локально для модального окна
                await fetchVerificationStatus();
                // Обновляем данные в контексте PaymentContext
                if (fetchVerificationStatusContext && typeof fetchVerificationStatusContext === 'function') {
                    await fetchVerificationStatusContext();
                }
            } else {
                showNotification(data.message || "Ошибка при подаче заявки", "error");
            }
        } catch (error) {
            console.error('Ошибка при подаче заявки:', error);
            showNotification("Произошла ошибка при подаче заявки", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const openImageViewer = (imageUrl) => {
        setViewingImage(imageUrl);
    };

    const closeImageViewer = () => {
        setViewingImage(null);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved':
                return <FaCheckCircle className="status-icon approved" />;
            case 'pending':
                return <FaClock className="status-icon pending" />;
            case 'rejected':
                return <FaTimes className="status-icon rejected" />;
            default:
                return null;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'approved':
                return "Одобрена";
            case 'pending':
                return "На рассмотрении";
            case 'rejected':
                return "Отклонена";
            default:
                return "Неизвестно";
        }
    };

    const canSubmitNewRequest = () => {
        return !verificationData?.verificationRequest ||
            verificationData.verificationRequest.status === 'rejected';
    };

    if (loading) {
        return (
            <div className="profile-modal-overlay">
                <div className="profile-modal profile-modal--verification">
                    <div className="verification-loading">
                        <div className="loading-spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="profile-modal-overlay" onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}>
                <div className="profile-modal profile-modal--verification" onClick={(e) => e.stopPropagation()}>
                    <div className="profile-modal-header">
                        <h3 className="profile-modal-title">Верификация аккаунта</h3>
                        <button className="profile-modal-close" onClick={onClose}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="12" fill="#23232e" />
                                <path d="M16 8L8 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                <path d="M8 8L16 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>

                    {/* Статус верификации */}
                    <div className="verification-status-section">
                        <div className="verification-status-card">
                            <div className="status-header">
                                <h4>Статус аккаунта</h4>
                                <div className={`status-badge ${verificationData?.isVerified ? 'verified' : 'unverified'}`}>
                                    {verificationData?.isVerified ? (
                                        <>
                                            <FaCheckCircle />
                                            <span>Верифицирован</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaTimes />
                                            <span>Не верифицирован</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Информация о текущей заявке */}
                    {verificationData?.verificationRequest && (
                        <div className="current-request-section">
                            <div className="request-card">
                                <div className="request-header">
                                    <h4>Текущая заявка</h4>
                                    <div className="request-status">
                                        {getStatusIcon(verificationData.verificationRequest.status)}
                                        <span>{getStatusText(verificationData.verificationRequest.status)}</span>
                                    </div>
                                </div>

                                <div className="request-details">
                                    <div className="detail-row">
                                        <span className="label">Имя:</span>
                                        <span className="value">
                                            {verificationData.verificationRequest.first_name} {verificationData.verificationRequest.last_name}
                                            {verificationData.verificationRequest.middle_name && ` ${verificationData.verificationRequest.middle_name}`}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Дата рождения:</span>
                                        <span className="value">
                                            {new Date(verificationData.verificationRequest.birth_date).toLocaleDateString('ru-RU')}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Дата подачи:</span>
                                        <span className="value">
                                            {new Date(verificationData.verificationRequest.created_at).toLocaleDateString('ru-RU')}
                                        </span>
                                    </div>
                                </div>

                                {/* Просмотр загруженных фото */}
                                <div className="request-photos">
                                    <h5>Загруженные документы</h5>
                                    <div className="photos-grid">
                                        {verificationData.verificationRequest.document_photo_path && (
                                            <div className="photo-item">
                                                <div className="photo-preview">
                                                    <img
                                                        src={`${API_URL}/api/verification/photo/${verificationData.verificationRequest.document_photo_path.split('/').pop()}`}
                                                        alt="Фото документа"
                                                    />
                                                    <button
                                                        className="view-photo-btn"
                                                        onClick={() => openImageViewer(`${API_URL}/api/verification/photo/${verificationData.verificationRequest.document_photo_path.split('/').pop()}`)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </div>
                                                <span className="photo-label">Фото документа</span>
                                            </div>
                                        )}
                                        {verificationData.verificationRequest.selfie_photo_path && (
                                            <div className="photo-item">
                                                <div className="photo-preview">
                                                    <img
                                                        src={`${API_URL}/api/verification/photo/${verificationData.verificationRequest.selfie_photo_path.split('/').pop()}`}
                                                        alt="Селфи с документом"
                                                    />
                                                    <button
                                                        className="view-photo-btn"
                                                        onClick={() => openImageViewer(`${API_URL}/api/verification/photo/${verificationData.verificationRequest.selfie_photo_path.split('/').pop()}`)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                </div>
                                                <span className="photo-label">Селфи с документом</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Комментарий администратора */}
                                {verificationData.verificationRequest.admin_comment && (
                                    <div className="admin-comment">
                                        <h5>Комментарий администратора</h5>
                                        <p>{verificationData.verificationRequest.admin_comment}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Форма подачи заявки */}
                    {canSubmitNewRequest() && (
                        <div className="verification-form-section">
                            {verificationData?.verificationRequest?.status === 'rejected' && (
                                <div className="rejected-notice">
                                    <p>Ваша предыдущая заявка была отклонена. Вы можете подать новую заявку.</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="verification-form">
                                <h4>Пройти верификацию</h4>

                                <div className="form-row">
                                    <div className="form-field">
                                        <label htmlFor="firstName">Имя *</label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label htmlFor="lastName">Фамилия *</label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-field">
                                        <label htmlFor="middleName">Отчество</label>
                                        <input
                                            type="text"
                                            id="middleName"
                                            name="middleName"
                                            value={formData.middleName}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-field">
                                        <label htmlFor="birthDate">Дата рождения *</label>
                                        <input
                                            type="date"
                                            id="birthDate"
                                            name="birthDate"
                                            value={formData.birthDate}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="file-uploads">
                                    <div className="file-upload-field">
                                        <label>Фото документа *</label>
                                        <div className="file-upload-area">
                                            <input
                                                type="file"
                                                id="documentPhoto"
                                                accept="image/*"
                                                onChange={(e) => handleFileChange(e, 'document')}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="documentPhoto" className="file-upload-btn">
                                                <FaUpload />
                                                Выбрать файл
                                            </label>
                                            {documentPreview && (
                                                <div className="file-preview">
                                                    <img src={documentPreview} alt="Предпросмотр документа" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="file-upload-field">
                                        <label>Селфи с документом *</label>
                                        <div className="file-upload-area">
                                            <input
                                                type="file"
                                                id="selfiePhoto"
                                                accept="image/*"
                                                onChange={(e) => handleFileChange(e, 'selfie')}
                                                style={{ display: 'none' }}
                                            />
                                            <label htmlFor="selfiePhoto" className="file-upload-btn">
                                                <FaUpload />
                                                Выбрать файл
                                            </label>
                                            {selfiePreview && (
                                                <div className="file-preview">
                                                    <img src={selfiePreview} alt="Предпросмотр селфи" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="verification-requirements">
                                    <h5>Требования к документам:</h5>
                                    <ul>
                                        <li>Фото документа должно быть четким и читаемым</li>
                                        <li>Все данные в документе должны быть видны</li>
                                        <li>На селфи должны быть видны ваше лицо и документ</li>
                                        <li>Максимальный размер файла: 10MB</li>
                                        <li>Поддерживаемые форматы: JPG, PNG, WebP</li>
                                    </ul>
                                </div>

                                <button
                                    type="submit"
                                    className="verification-submit-btn"
                                    disabled={submitting}
                                >
                                    {submitting ? "Отправка..." : "Подать заявку"}
                                </button>
                            </form>
                        </div>
                    )}

                    {/* Если заявка на рассмотрении и аккаунт не верифицирован */}
                    {!canSubmitNewRequest() && !verificationData?.isVerified && (
                        <div className="pending-notice">
                            <FaClock className="pending-icon" />
                            <h4>Заявка на рассмотрении</h4>
                            <p>Ваша заявка находится на рассмотрении. Мы свяжемся с вами в ближайшее время.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Модальное окно для просмотра изображений */}
            {viewingImage && (
                <div className="image-viewer-overlay" onClick={closeImageViewer}>
                    <div className="image-viewer">
                        <button className="image-viewer-close" onClick={closeImageViewer}>
                            <FaTimes />
                        </button>
                        <img src={viewingImage} alt="Просмотр документа" />
                    </div>
                </div>
            )}
        </>
    );
};

export default VerificationModal;
